import React, { useState, useEffect } from 'react';
import { X, Pencil, Upload, Loader2, Copy, Check, Building2, FileText, Banknote, Smartphone } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { editTransaction } from '../../../store/slices/ordersSlice';
import { setSnackbar } from '../../../store/slices/uiSlice';
import { storageEnv } from '../../../config/firebase';
import { api, ocrService } from '../../../services/api';
import { API_ENDPOINTS } from '../../../config/api';
import { toDdmmyyyy } from '../../../utils/format';
import { EditableField } from '../Users/CreateOrderModal';
import SmartImage from '../../common/SmartImage';
import { ImageSourceTabs, PasteUrlBox } from '../../common/ImageSourceTabs';
import {
    PaymentMethodType, TransferMode,
    UploadState, emptyUpload, uploadToFirebase,
    fieldsForPaymentType,
    extractTextFromExistingImage,
    isValidDdmmyyyy, ddmmyyyyToIso, isoToDdmmyyyy,
} from '../Users/paymentFormUtils';

interface EditTransactionModalProps {
    orderId: string;
    transaction: any;
    onClose: () => void;
    onSuccess?: () => void;
    // When true, edits go to the employee endpoint (own PENDING_SUBMISSION order),
    // which keeps the order held instead of advancing it to admin verification.
    isEmployee?: boolean;
    // Investor's mobile — used to fetch the coin wallet balance when editing a
    // COINS_REDEEM transaction so the redemption can be validated/reconciled.
    userMobile?: string;
    // The most this transaction's amount can be edited up to — order total minus every
    // OTHER transaction on it (i.e. what's left if this one's current amount is excluded).
    // Drives the "Max ₹X" helper text / cap validation on the Amount field.
    // Omit to skip that display (e.g. no order context available).
    maxAmount?: number;
}

const PAYMENT_METHOD_OPTIONS: { key: PaymentMethodType; label: string; icon: React.ReactNode }[] = [
    { key: 'BANK_TRANSFER', label: 'Bank Transfer', icon: <Building2 size={15} /> },
    { key: 'CHEQUE', label: 'Cheque', icon: <FileText size={15} /> },
    { key: 'CASH', label: 'Cash', icon: <Banknote size={15} /> },
    { key: 'UPI', label: 'UPI', icon: <Smartphone size={15} /> },
];

function numberToWords(num: number): string {
    if (!num || num <= 0 || !isFinite(num)) return '';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
        'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const below100 = (n: number) => n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    const below1000 = (n: number) => n < 100 ? below100(n) : ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + below100(n % 100) : '');
    const n = Math.floor(num);
    const parts: string[] = [];
    if (Math.floor(n / 10000000)) parts.push(below1000(Math.floor(n / 10000000)) + ' Crore');
    if (Math.floor((n % 10000000) / 100000)) parts.push(below1000(Math.floor((n % 10000000) / 100000)) + ' Lakh');
    if (Math.floor((n % 100000) / 1000)) parts.push(below1000(Math.floor((n % 100000) / 1000)) + ' Thousand');
    if (n % 1000) parts.push(below1000(n % 1000));
    return parts.join(' ') + ' Only';
}

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({
    orderId,
    transaction,
    onClose,
    onSuccess,
    isEmployee,
    userMobile,
    maxAmount,
}) => {
    const dispatch = useAppDispatch();
    const adminMobile = useAppSelector((state: RootState) => state.auth.adminMobile);
    const editTransactionLoading = useAppSelector((state: RootState) => state.orders.editTransactionLoading);

    const txd = { ...(transaction?.transaction || {}), ...transaction };
    const txId = txd.id || txd.transactionId || transaction?.id;

    // Coin redemptions are edited coin-only (method locked to COINS_REDEEM). 1 coin = ₹1.
    const isCoins = (txd.paymentMethod || txd.paymentType) === 'COINS_REDEEM';
    const originalCoins = Number(txd.amount || 0);

    // CASH_DEPOSIT isn't its own top-level method — it's BANK_TRANSFER with transferMode
    // 'CASH_DEPOSIT', same as Create Order / Update Payment. Remap on the way in.
    const storedMethod = txd.paymentMethod || txd.paymentType || 'BANK_TRANSFER';
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>(
        storedMethod === 'CASH_DEPOSIT' ? 'BANK_TRANSFER' : (storedMethod as PaymentMethodType)
    );
    const [transferMode, setTransferMode] = useState<TransferMode>(
        storedMethod === 'CASH_DEPOSIT' ? 'CASH_DEPOSIT' : (txd.transferMode || txd.transfer_mode || 'NEFT')
    );
    const [utrNumber, setUtrNumber] = useState(txd.utrNumber || txd.utr_number || txd.utr || txd.chequeNo || txd.cheque_no || '');
    const [accountHolderName, setAccountHolderName] = useState(txd.accountHolderName || txd.account_holder_name || '');
    const [bankName, setBankName] = useState(txd.bankName || txd.bank_name || '');
    const [cashierName, setCashierName] = useState(txd.cashierName || txd.cashier_name || '');
    const [cashierPhone, setCashierPhone] = useState(txd.cashierPhone || txd.cashier_phone || '');
    const [paidBy, setPaidBy] = useState(txd.paidBy || txd.paid_by || 'Self');

    // Amount is editable only in employee mode; admin edits leave it unchanged (read-only).
    const [amount, setAmount] = useState(String(txd.amount || ''));
    // Transaction date is stored as DD-MM-YYYY; <input type="text"> expects DD-MM-YYYY.
    // Some sources (e.g. OCR-extracted receipts) return DD/MM/YYYY instead — normalize
    // on the way in so the format validator below doesn't flag an otherwise-valid date.

    const rawTransactionDate = txd.transactionDate || txd.cash_payment_date || txd.chequeDate || '';
    const [transactionDate, setTransactionDate] = useState(rawTransactionDate ? toDdmmyyyy(rawTransactionDate) : '');

    const [reason, setReason] = useState('');

    // Derive a single "proof image" from ANY image field the transaction may have — used to
    // seed whichever upload slot is relevant so the image preview works against the
    // document already on file.
    const existingProofImg =
        txd.paymentScreenshotUrl ||
        txd.voucherImageUrl || txd.voucher_image_url ||
        txd.chequeFrontImage || txd.frontImageUrl || txd.front_image_url ||
        null;
    const existingBackImg = txd.chequeBackImage || txd.backImageUrl || txd.back_image_url || null;

    // Only the slot matching the transaction's ORIGINAL method gets seeded with the existing
    // image — the other three start empty, otherwise switching methods would show the same
    // image as if it were already uploaded for a method it was never actually submitted for.
    const [screenshotUpload, setScreenshotUpload] = useState<UploadState>({ ...emptyUpload(), url: (storedMethod === 'BANK_TRANSFER' || storedMethod === 'CASH_DEPOSIT') ? (existingProofImg || '') : '' });
    const [voucherUpload, setVoucherUpload] = useState<UploadState>({ ...emptyUpload(), url: storedMethod === 'CASH' ? (existingProofImg || '') : '' });
    const [chequeFrontUpload, setChequeFrontUpload] = useState<UploadState>({ ...emptyUpload(), url: storedMethod === 'CHEQUE' ? (existingProofImg || '') : '' });
    const [chequeBackUpload, setChequeBackUpload] = useState<UploadState>({ ...emptyUpload(), url: storedMethod === 'CHEQUE' ? (existingBackImg || '') : '' });
    const [upiScreenshotUpload, setUpiScreenshotUpload] = useState<UploadState>({ ...emptyUpload(), url: storedMethod === 'UPI' ? (existingProofImg || '') : '' });

    const [utrDuplicate, setUtrDuplicate] = useState(false);
    const [utrChecking, setUtrChecking] = useState(false);
    const [ocrText, setOcrText] = useState<string | null>(null);
    const [ocrLoading, setOcrLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Per-field "checked against the document" acknowledgment — editing a field after
    // verifying it drops it back to unverified, since the (now different) value hasn't
    // been checked against the image yet.
    const [verifiedFields, setVerifiedFields] = useState<Set<string>>(() => {
        const initial = new Set<string>();
        if (!isEmployee) {
            initial.add('amount');
            initial.add('txDate');
        }
        return initial;
    });
    const markVerified = (key: string) => setVerifiedFields(prev => new Set(prev).add(key));
    const unverify = (key: string) => setVerifiedFields(prev => { if (!prev.has(key)) return prev; const next = new Set(prev); next.delete(key); return next; });

    // Fetch the investor's coin wallet balance when editing a coins redemption.
    const [availableCoins, setAvailableCoins] = useState<number | null>(null);
    useEffect(() => {
        if (!isCoins || !userMobile) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get<any>(API_ENDPOINTS.getCoinTransaction(userMobile));
                const rem = res?.data?.stats?.remaining_coins;
                if (!cancelled) setAvailableCoins(Math.floor(Number(rem) || 0));
            } catch {
                if (!cancelled) setAvailableCoins(0);
            }
        })();
        return () => { cancelled = true; };
    }, [isCoins, userMobile]);

    // Reducing coins refunds the difference to the wallet; increasing debits it.
    // You can raise the redemption up to what's already on this tx + wallet balance.
    const coinDelta = isCoins ? (Number(amount) || 0) - originalCoins : 0;
    const maxCoins = availableCoins === null ? Infinity : originalCoins + availableCoins;
    const coinsExceed = isCoins && (Number(amount) || 0) > maxCoins;

    const isCashDeposit = paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT';

    // Whichever upload slot matches the currently selected method — the single "proof
    // image" the left panel shows and lets the admin re-upload.
    const activeUpload = paymentMethod === 'CASH' ? voucherUpload
        : paymentMethod === 'CHEQUE' ? chequeFrontUpload
            : paymentMethod === 'UPI' ? upiScreenshotUpload
                : screenshotUpload;
    const setActiveUpload = paymentMethod === 'CASH' ? setVoucherUpload
        : paymentMethod === 'CHEQUE' ? setChequeFrontUpload
            : paymentMethod === 'UPI' ? setUpiScreenshotUpload
                : setScreenshotUpload;
    const activeUploadLabel = paymentMethod === 'CASH' ? 'Voucher Image'
        : paymentMethod === 'CHEQUE' ? 'Cheque Front Image'
            : paymentMethod === 'UPI' ? 'Payment Screenshot'
                : isCashDeposit ? 'Deposit Slip / Screenshot'
                    : 'Payment Document';

    const handleFileUpload = async (
        file: File,
        setUpload: React.Dispatch<React.SetStateAction<UploadState>>,
        pathSuffix: string,
    ) => {
        setUpload(prev => ({ ...prev, file, url: '', uploading: true, error: '', progress: 0 }));
        try {
            let finalFile = file;
            if (file.type.startsWith('image/')) {
                try {
                    finalFile = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                } catch { /* use original */ }
            }
            const path = `animalkart/${storageEnv}/tx-edits/${orderId}/${Date.now()}_${pathSuffix}_${file.name}`;
            const url = await uploadToFirebase(finalFile, path, pct => setUpload(prev => ({ ...prev, progress: pct })));
            setUpload(prev => ({ ...prev, file: finalFile, url, uploading: false }));
        } catch {
            setUpload(prev => ({ ...prev, uploading: false, error: 'Upload failed. Please try again.' }));
        }
    };

    const handleUrlSubmit = async (url: string, setUpload: React.Dispatch<React.SetStateAction<UploadState>>) => {
        if (url.includes('firebasestorage.googleapis.com')) {
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
            return;
        }
        try {
            setUpload(prev => ({ ...prev, file: null, url: '', uploading: true, error: '', progress: 0 }));
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const fileName = "pasted_image" + (blob.type.includes('png') ? '.png' : '.jpg');
            const file = new File([blob], fileName, { type: blob.type });
            
            let pathSuffix = 'proof';
            if (setUpload === setChequeFrontUpload) pathSuffix = 'cheque_front';
            else if (setUpload === setChequeBackUpload) pathSuffix = 'cheque_back';
            else if (setUpload === setVoucherUpload) pathSuffix = 'voucher';
            else if (setUpload === setUpiScreenshotUpload) pathSuffix = 'upi_screenshot';
            
            handleFileUpload(file, setUpload, pathSuffix);
        } catch (err: any) {
            console.warn("Could not download pasted image for Firebase upload (likely CORS):", err);
            setUpload(prev => ({ ...prev, file: null, url, uploading: false, error: '', progress: 100 }));
        }
    };

    const anyUploading = screenshotUpload.uploading || voucherUpload.uploading ||
        chequeFrontUpload.uploading || chequeBackUpload.uploading || upiScreenshotUpload.uploading;

    // Runs OCR against whichever image is currently active — a freshly picked file goes
    // straight to the OCR service; a pre-existing (already-uploaded) image is fetched via
    // the CORS proxy first, since there's no local File object for it.
    useEffect(() => {
        const url = activeUpload.url;
        if (!url) { setOcrText(null); setOcrLoading(false); return; }
        let cancelled = false;
        setOcrLoading(true);
        const run = activeUpload.file
            ? ocrService.extractText(activeUpload.file).then(r => r?.text || null)
            : extractTextFromExistingImage(url);
        run
            .then(text => { if (!cancelled) setOcrText(text); })
            .catch(() => { if (!cancelled) setOcrText(null); })
            .finally(() => { if (!cancelled) setOcrLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeUpload.url, activeUpload.file]);

    // Real-time duplicate check: warn if the entered UTR / cheque no / UPI reference is
    // already used by a PAID order or APPROVED transaction (same pattern as Create Order).
    useEffect(() => {
        const v = (utrNumber || '').trim();
        const needsRef = paymentMethod === 'UPI' || paymentMethod === 'CHEQUE' || paymentMethod === 'BANK_TRANSFER';
        if (!needsRef || v.length < 2) { setUtrDuplicate(false); setUtrChecking(false); return; }
        let cancelled = false;
        setUtrChecking(true);
        const t = setTimeout(async () => {
            try {
                const url = API_ENDPOINTS.checkTransactionReference(v) + (txId ? `&excludeTransactionId=${encodeURIComponent(txId)}` : '');
                const res = await api.get(url);
                if (!cancelled) setUtrDuplicate(!!res.data?.exists);
            } catch {
                if (!cancelled) setUtrDuplicate(false);
            } finally {
                if (!cancelled) setUtrChecking(false);
            }
        }, 500);
        return () => { cancelled = true; clearTimeout(t); };
    }, [utrNumber, paymentMethod, isCashDeposit, txId]);

    // Clears method-specific fields when the payment method actually changes (not on mount) —
    // same behavior as Create Order / Update Payment when switching methods.
    const resetPaymentSubFields = () => {
        setTransferMode('NEFT');
        setUtrNumber('');
        setAccountHolderName('');
        setBankName('');
        setCashierName('');
        setCashierPhone('');
        setPaidBy('Self');
        // Amount/Date are locked for admins — clearing them here would strand the field
        // permanently blank, since admins have no path to refill it. Employees can retype.
        if (isEmployee) {
            setAmount('');
            setTransactionDate('');
        }
        setScreenshotUpload(emptyUpload());
        setVoucherUpload(emptyUpload());
        setChequeFrontUpload(emptyUpload());
        setChequeBackUpload(emptyUpload());
        setUpiScreenshotUpload(emptyUpload());
        setVerifiedFields(() => {
            const next = new Set<string>();
            if (!isEmployee) {
                next.add('amount');
                next.add('txDate');
            }
            return next;
        });
    };

    // Every field the Extracted Details box shows for the current method must be Verified
    // before saving — mirrors the required-field set isFormValid checks below. Cash Deposit
    // shows Paid By/UTR/Bank Name too, but they're optional, so they're excluded here.
    const requiredFieldKeys = (): string[] => {
        const keys = ['amount', 'txDate', ...fieldsForPaymentType(paymentMethod, paidBy).map(f => f.key)];
        if (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT') {
            return keys.filter(k => !['paidBy', 'utrNumber', 'accountHolderName', 'bankName'].includes(k));
        }
        return keys;
    };
    const allFieldsVerified = isCoins || requiredFieldKeys().every(k => verifiedFields.has(k));

    const isFormValid = (() => {
        if (!reason.trim()) return false;
        if (!isCoins && !allFieldsVerified) return false;
        if (isEmployee && !(Number(amount) > 0)) return false;
        if (isEmployee && maxAmount != null && Number(amount) > maxAmount) return false;
        if (isCoins) return !coinsExceed;

        switch (paymentMethod) {
            case 'BANK_TRANSFER':
                if (!transferMode) return false;
                if (transferMode === 'CASH_DEPOSIT') return true;
                if (paidBy !== 'Self' && !accountHolderName.trim()) return false;
                return !!(bankName.trim() && utrNumber.trim());
            case 'CASH':
                return !!(cashierName.trim() && cashierPhone.trim().length === 10);
            case 'UPI':
                return !!utrNumber.trim();
            case 'CHEQUE':
                return utrNumber.trim().length >= 2;
            default:
                return true;
        }
    })();

    const handleSubmit = async () => {
        if (!isCoins && transactionDate && !isValidDdmmyyyy(transactionDate)) {
            dispatch(setSnackbar({ message: 'Enter transaction date in DD-MM-YYYY format', type: 'error' }));
            return;
        }
        if (!txId) {
            dispatch(setSnackbar({ message: 'Transaction ID not found', type: 'error' }));
            return;
        }
        if (!reason.trim()) {
            dispatch(setSnackbar({ message: 'Please provide a reason for the edit', type: 'error' }));
            return;
        }

        const effectivePaymentMethod = (paymentMethod === 'BANK_TRANSFER' && transferMode === 'CASH_DEPOSIT') ? 'CASH_DEPOSIT' : paymentMethod;

        const payload: any = {
            orderId,
            transactionId: txId,
            paymentMethod: effectivePaymentMethod,
            reason: reason.trim(),
            adminMobile,
            asEmployee: isEmployee || undefined,
            transactionDate: transactionDate || undefined,
            paid_by: paidBy || undefined,
            // Amount is editable only in employee mode; admin edits leave it unchanged.
            amount: isEmployee ? (Number(amount) || undefined) : undefined,
        };

        if (paymentMethod === 'BANK_TRANSFER') {
            if (transferMode) payload.transferMode = transferMode;
            if (utrNumber) payload.utrNumber = utrNumber;
            if (paidBy !== 'Self' && accountHolderName) payload.accountHolderName = accountHolderName;
            if (bankName) payload.bankName = bankName;
            if (screenshotUpload.url) payload.paymentScreenshotUrl = screenshotUpload.url;
        } else if (paymentMethod === 'CASH') {
            if (cashierName) payload.cashierName = cashierName;
            if (cashierPhone) payload.cashierPhone = cashierPhone;
            if (voucherUpload.url) payload.voucherImageUrl = voucherUpload.url;
        } else if (paymentMethod === 'UPI') {
            if (utrNumber) payload.utrNumber = utrNumber;
            if (paidBy !== 'Self' && accountHolderName) payload.accountHolderName = accountHolderName;
            if (upiScreenshotUpload.url) payload.paymentScreenshotUrl = upiScreenshotUpload.url;
        } else if (paymentMethod === 'CHEQUE') {
            if (utrNumber) payload.utrNumber = utrNumber;
            if (transactionDate) payload.chequeDate = transactionDate;
            if (chequeFrontUpload.url) payload.chequeFrontImage = chequeFrontUpload.url;
            if (chequeBackUpload.url) payload.chequeBackImage = chequeBackUpload.url;
        }

        try {
            // The edit endpoints report validation failures (e.g. insufficient coins)
            // as { statuscode >= 400 } inside an HTTP 200 body, so check it explicitly.
            const result: any = await dispatch(editTransaction(payload)).unwrap();
            if (result?.statuscode && Number(result.statuscode) >= 400) {
                dispatch(setSnackbar({ message: result.message || 'Failed to update transaction', type: 'error' }));
                return;
            }
            dispatch(setSnackbar({ message: 'Transaction updated successfully!', type: 'success' }));
            onClose();
            onSuccess?.();
        } catch (err: any) {
            dispatch(setSnackbar({ message: err || 'Failed to update transaction', type: 'error' }));
        }
    };

    const fieldValue = (key: string): string => {
        switch (key) {
            case 'transferMode': return transferMode;
            case 'paidBy': return paidBy;
            case 'utrNumber': return utrNumber;
            case 'accountHolderName': return accountHolderName;
            case 'bankName': return bankName;
            case 'cashierName': return cashierName;
            case 'cashierPhone': return cashierPhone;
            default: return '';
        }
    };
    const fieldSetter = (key: string) => (v: string) => {
        switch (key) {
            case 'transferMode': setTransferMode(v as TransferMode); break;
            case 'paidBy': setPaidBy(v); break;
            case 'utrNumber': setUtrNumber(v); break;
            case 'accountHolderName': setAccountHolderName(v); break;
            case 'bankName': setBankName(v); break;
            case 'cashierName': setCashierName(v); break;
            // Same 10-digit constraint the other payment forms apply to this field.
            case 'cashierPhone': setCashierPhone(v.replace(/\D/g, '').slice(0, 10)); break;
        }
        unverify(key);
    };

    const handleCopy = () => {
        if (!ocrText) return;
        navigator.clipboard.writeText(ocrText);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const inputClass = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white outline-none focus:border-[#1B4332] transition-all";
    const labelClass = "block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1";

    return (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-[1200] p-4" onClick={onClose}>
            <div
                className={`bg-white w-full ${isCoins ? 'max-w-[560px]' : 'max-w-5xl'} rounded-xl shadow-2xl overflow-hidden border border-slate-200 max-h-[92vh] flex flex-col`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100 bg-slate-50/40 shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Pencil size={14} className="text-amber-600" />
                        </div>
                        <div>
                            <h3 className="text-[14px] font-bold text-slate-800 m-0">Edit Transaction</h3>
                            <p className="text-[10px] text-slate-400 m-0 font-medium break-all">{txId || 'Unknown ID'}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-slate-200/50 rounded-full transition-all bg-transparent border-none cursor-pointer"
                    >
                        <X size={15} className="text-slate-400" />
                    </button>
                </div>

                {isCoins ? (
                    <div className="overflow-y-auto flex-1 p-5 space-y-4">
                        <div>
                            <div className="flex items-center justify-between">
                                <label className="block text-xs font-medium text-slate-600">
                                    Coins to Redeem <span className="text-amber-600 font-normal">(Available: {availableCoins === null ? '…' : availableCoins.toLocaleString('en-IN')} coins)</span>
                                    {isEmployee && <span className="text-red-500"> *</span>}
                                </label>
                                {isEmployee && (
                                    <button type="button"
                                        onClick={() => setAmount(String(maxCoins))}
                                        className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider">MAX</button>
                                )}
                            </div>
                            {isEmployee ? (
                                <input
                                    type="number"
                                    min={1}
                                    value={amount}
                                    onWheel={e => (e.target as HTMLInputElement).blur()}
                                    onKeyDown={e => { if (['-', '+', 'e', 'E', '.', 'ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault(); }}
                                    onChange={e => {
                                        const raw = e.target.value.replace(/[^0-9]/g, '');
                                        if (raw !== '' && Number(raw) > maxCoins) return;
                                        setAmount(raw);
                                    }}
                                    placeholder={`Enter coins (Available: ${availableCoins === null ? '…' : availableCoins.toLocaleString('en-IN')})`}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                            ) : (
                                <input type="number" className={`${inputClass} bg-slate-50 text-slate-400 cursor-not-allowed`} value={amount} readOnly />
                            )}
                            {Number(amount) > 0 && (
                                <p className="mt-1.5 text-[11px] font-bold text-slate-900 pl-0.5">{numberToWords(Number(amount)).replace(/ Only$/, '')} Coins Only</p>
                            )}
                            <div className="mt-1.5 space-y-0.5 pl-0.5">
                                {coinsExceed ? (
                                    <p className="text-[11px] font-bold text-red-500">
                                        Only {(availableCoins ?? 0).toLocaleString('en-IN')} coins available (max {maxCoins.toLocaleString('en-IN')}).
                                    </p>
                                ) : coinDelta < 0 ? (
                                    <p className="text-[11px] font-bold text-emerald-600">
                                        {Math.abs(coinDelta).toLocaleString('en-IN')} coins will be refunded to the wallet.
                                    </p>
                                ) : coinDelta > 0 ? (
                                    <p className="text-[11px] font-bold text-amber-600">
                                        {coinDelta.toLocaleString('en-IN')} additional coins will be debited.
                                    </p>
                                ) : null}
                            </div>
                        </div>

                        {/* Reason (required) */}
                        <div>
                            <label className={labelClass}>Reason for Edit <span className="text-red-500 normal-case">*</span></label>
                            <textarea
                                className={`${inputClass} resize-none min-h-[72px]`}
                                placeholder="Explain why you are editing this transaction..."
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="overflow-y-auto flex-1 grid grid-cols-1 lg:grid-cols-2">
                        {/* Left: proof image — full size for a clear, unobstructed view */}
                        <div className="p-4 bg-slate-100/50 flex flex-col gap-3 lg:border-r border-slate-100">
                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{activeUploadLabel}</span>
                            <ImageSourceTabs onModeChange={() => setActiveUpload(emptyUpload())}>
                                {mode => mode === 'url' ? (
                                    <PasteUrlBox url={activeUpload.url} onSubmit={url => handleUrlSubmit(url, setActiveUpload)} onClear={() => setActiveUpload(emptyUpload())} loading={activeUpload.uploading} previewClassName="max-w-full max-h-[70vh] object-contain" />
                                ) : (
                                    <label className="block min-h-[420px] rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden p-2 cursor-pointer hover:border-[#1B4332] transition-colors">
                                        <input type="file" accept="image/*" className="hidden"
                                            onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, setActiveUpload, 'proof'); e.currentTarget.value = ''; }} />
                                        {activeUpload.uploading ? (
                                            <div className="flex flex-col items-center gap-2 text-[#1B4332]">
                                                <Loader2 size={22} className="animate-spin" />
                                                <span className="text-xs font-semibold">Uploading... {activeUpload.progress}%</span>
                                            </div>
                                        ) : activeUpload.url ? (
                                            <SmartImage src={activeUpload.url} alt="Proof" className="max-w-full max-h-[70vh] object-contain" />
                                        ) : (
                                            <div className="flex flex-col items-center gap-2 text-slate-400">
                                                <Upload size={22} />
                                                <span className="text-xs font-semibold">Click to upload</span>
                                            </div>
                                        )}
                                    </label>
                                )}
                            </ImageSourceTabs>
                            {activeUpload.error && <p className="text-[10px] text-red-500">{activeUpload.error}</p>}

                            {paymentMethod === 'CHEQUE' && (
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Cheque Back Image (Optional)</span>
                                    <ImageSourceTabs onModeChange={() => setChequeBackUpload(emptyUpload())}>
                                        {mode => mode === 'url' ? (
                                            <PasteUrlBox url={chequeBackUpload.url} onSubmit={url => handleUrlSubmit(url, setChequeBackUpload)} onClear={() => setChequeBackUpload(emptyUpload())} loading={chequeBackUpload.uploading} previewClassName="w-full h-24 object-cover rounded-lg border border-slate-200 bg-white" />
                                        ) : (
                                            <label className="block cursor-pointer">
                                                <input type="file" accept="image/*" className="hidden"
                                                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, setChequeBackUpload, 'cheque_back'); e.currentTarget.value = ''; }} />
                                                {chequeBackUpload.url ? (
                                                    <SmartImage src={chequeBackUpload.url} alt="Cheque back" className="w-full h-24 object-cover rounded-lg border border-slate-200 bg-white" />
                                                ) : (
                                                    <div className="w-full h-24 flex flex-col items-center justify-center gap-1 text-slate-400 rounded-lg border border-dashed border-slate-200 bg-white hover:border-[#1B4332] transition-colors">
                                                        <Upload size={16} />
                                                        <span className="text-[10px] font-semibold">Click to upload</span>
                                                    </div>
                                                )}
                                            </label>
                                        )}
                                    </ImageSourceTabs>
                                </div>
                            )}
                        </div>

                        {/* Right: form box + extracted-text box */}
                        <div className="p-4 space-y-4">
                            {/* Box 1: mode chips + editable fields + reason */}
                            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                                <div className="flex flex-wrap gap-2">
                                    {PAYMENT_METHOD_OPTIONS.map(({ key, label, icon }) => (
                                        <button key={key} type="button" onClick={() => { setPaymentMethod(key); resetPaymentSubFields(); }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${paymentMethod === key ? 'bg-[#527d60] text-white border-[#527d60] shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-[#527d60]/60 hover:text-[#527d60]'}`}>
                                            {icon}{label}
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-2">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Extracted Details</span>
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 divide-y divide-slate-100">
                                        <EditableField
                                            label="Amount" type="number" value={amount}
                                            onChange={v => { setAmount(v.replace(/[^0-9]/g, '')); unverify('amount'); }}
                                            disabled={!isEmployee}
                                            required
                                            onApply={() => markVerified('amount')}
                                            justApplied={verifiedFields.has('amount')}
                                            helperText={[
                                                maxAmount != null ? `Max ₹${maxAmount.toLocaleString('en-IN')}` : null,
                                                Number(amount) > 0 ? numberToWords(Number(amount)) : null,
                                            ].filter(Boolean).join(' · ') || undefined}
                                            errorText={isEmployee && maxAmount != null && Number(amount) > maxAmount ? `Cannot exceed ₹${maxAmount.toLocaleString('en-IN')}` : undefined}
                                        />
                                        <EditableField
                                            label="Transaction Date" type="date" value={ddmmyyyyToIso(transactionDate)}
                                            onChange={v => { setTransactionDate(isoToDdmmyyyy(v)); unverify('txDate'); }}
                                            disabled={!isEmployee}
                                            required
                                            onApply={() => markVerified('txDate')}
                                            justApplied={verifiedFields.has('txDate')}
                                            errorText={(transactionDate && !isValidDdmmyyyy(transactionDate)) ? 'Format: DD-MM-YYYY' : undefined}
                                        />
                                        {fieldsForPaymentType(paymentMethod, paidBy, transferMode).map(spec => (
                                            <EditableField
                                                key={spec.key}
                                                label={spec.label}
                                                type={spec.type}
                                                options={spec.options}
                                                isBank={spec.key === 'bankName'}
                                                required={spec.required}
                                                onApply={() => markVerified(spec.key)}
                                                justApplied={verifiedFields.has(spec.key)}
                                                value={fieldValue(spec.key)}
                                                onChange={fieldSetter(spec.key)}
                                                errorText={
                                                    spec.key === 'utrNumber' && utrDuplicate ? 'This transaction number already exists.'
                                                    : spec.key === 'cashierPhone' && cashierPhone && cashierPhone.length !== 10 ? 'Enter a valid 10-digit mobile number'
                                                    : undefined
                                                }
                                                helperText={spec.key === 'utrNumber' && utrChecking ? 'Checking…' : undefined}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Reason (required) */}
                                <div>
                                    <label className={labelClass}>Reason for Edit <span className="text-red-500 normal-case">*</span></label>
                                    <textarea
                                        className={`${inputClass} resize-none min-h-[72px]`}
                                        placeholder="Explain why you are editing this transaction..."
                                        value={reason}
                                        onChange={e => setReason(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Box 2: Full Extracted Text */}
                            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Full Extracted Text</span>
                                    {ocrText && (
                                        <button type="button" onClick={handleCopy}
                                            className="flex items-center gap-1 text-[10px] font-bold text-[#527d60] hover:text-[#43674f] transition-colors">
                                            {copied ? <Check size={11} /> : <Copy size={11} />}
                                            {copied ? 'Copied' : 'Copy'}
                                        </button>
                                    )}
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 max-h-40 overflow-y-auto">
                                    {ocrLoading ? (
                                        <div className="flex items-center gap-1.5 text-slate-400 text-[11px]">
                                            <Loader2 size={11} className="animate-spin" /> Reading image...
                                        </div>
                                    ) : (
                                        <pre className="text-[11px] text-slate-700 whitespace-pre-wrap font-mono m-0">{ocrText || '—'}</pre>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between gap-3 shrink-0">
                    <p className="text-[10px] text-slate-400 font-medium">
                        This edit will be logged in order history.
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={editTransactionLoading}
                            className="px-4 py-2 rounded-lg text-[11px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all border-none cursor-pointer disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={editTransactionLoading || !isFormValid || anyUploading || utrDuplicate}
                            title={
                                !isCoins && !allFieldsVerified ? 'Verify every field before saving'
                                    : !reason.trim() ? 'A reason for the edit is required'
                                        : utrDuplicate ? 'This transaction number already exists'
                                            : undefined
                            }
                            className="px-5 py-2 rounded-lg text-[11px] font-bold text-white bg-[#527d60] hover:bg-[#43674f] transition-all border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {editTransactionLoading ? 'Saving...' : anyUploading ? 'Uploading...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditTransactionModal;
